import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import api from "../api/axiosInstance";
import { normaliseName } from "../utils/categoryOptions";

/**
 * Marketplace (Super Admin) categories the store's products use but the
 * store hasn't added itself. They show on the store's site, but only the
 * store's own categories can be ordered in its bar or made Featured /
 * Trending - so offer to add them (#276).
 * @param {Array<{business_id?: string|null, category: string, subcategory: string, type?: string, column_header?: string}>} rows
 *   from /category/list?business_id=<id>&include_platform=used
 * @returns {Array<{category: string, rows: Array}>}
 */
export const findUnaddedMarketplaceCategories = (rows = []) => {
    const own = new Set(rows.filter((row) => row.business_id).map((row) => normaliseName(row.category)));
    const groups = new Map();
    rows.forEach((row) => {
        if (row.business_id) return;
        const key = normaliseName(row.category);
        if (!key || own.has(key)) return;
        if (!groups.has(key)) groups.set(key, { category: String(row.category).trim(), rows: [] });
        groups.get(key).rows.push(row);
    });
    return [...groups.values()].sort((a, b) => a.category.localeCompare(b.category, "en", { sensitivity: "base" }));
};

function UsedMarketplaceCategories({ businessId, onAdded, refreshKey }) {
    const { t } = useTranslation();
    const [groups, setGroups] = useState([]);
    const [adding, setAdding] = useState("");

    useEffect(() => {
        if (!businessId) return;
        api.get("/category/list", { params: { business_id: businessId, include_platform: "used", limit: 100000 } })
            .then((res) => {
                const rows = res.data.status === "success" && Array.isArray(res.data.result) ? res.data.result : [];
                setGroups(findUnaddedMarketplaceCategories(rows));
            })
            .catch((err) => console.error("Error fetching marketplace categories in use:", err));
    }, [businessId, refreshKey]);

    const addToStore = async (group) => {
        setAdding(group.category);
        try {
            // One store row per marketplace subcategory the store uses; the
            // API keeps the Super Admin spelling and doesn't count them
            // towards the category limit.
            for (const row of group.rows) {
                await api.post("/category/create", {
                    category: group.category,
                    subcategory: row.subcategory,
                    type: row.type || "gift",
                    column_header: row.column_header || `${group.category} Collection`,
                    is_featured: false,
                    is_trending: false,
                    business_id: businessId,
                });
            }
            toast.success(t("categoryTab.usedMarketplaceAdded", { name: group.category }));
            setGroups((current) => current.filter((g) => g.category !== group.category));
            if (onAdded) onAdded();
        } catch (err) {
            toast.error(err.response?.data?.error?.message || t("categoryTab.usedMarketplaceAddFailed"));
        }
        setAdding("");
    };

    if (!groups.length) return null;

    return (
        <div className="card mb-3">
            <div className="card-body">
                <h5 className="card-title mb-1">{t("categoryTab.usedMarketplaceTitle")}</h5>
                <p className="text-muted small mb-2">{t("categoryTab.usedMarketplaceHint")}</p>
                {groups.map((group) => (
                    <div key={group.category} className="d-flex align-items-center justify-content-between border-top py-2">
                        <span>
                            <strong>{group.category}</strong>
                            <span className="text-muted small ml-2">
                                {group.rows.map((row) => row.subcategory).join(", ")}
                            </span>
                        </span>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            disabled={adding !== ""}
                            onClick={() => addToStore(group)}
                        >
                            {adding === group.category ? t("form.processing") : t("categoryTab.usedMarketplaceAdd")}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default UsedMarketplaceCategories;
